import React, { useState } from "react";
import AdminTicketEditModal from "./AdminTicketEditModal";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  Chip,
  Tooltip,
} from "@mui/material";
import type {
  HelpDeskTicket,
  TicketStatus,
  TicketPriority,
} from "../../Models/helpDesk";
import {
  useHelpDeskAccess,
  useMyTickets,
  useManagedTickets,
} from "../../Query/useHelpDesk";
import color from "../../Constants/colors";
import CreateTicketModal from "./CreateTicketModal";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const visibleStyles = {
  color: `${color.black} !important`,
  "& .MuiTypography-root": {
    color: `${color.black} !important`,
  },
  "& .MuiTypography-h4, & .MuiTypography-h6, & .MuiTypography-subtitle1, & .MuiTypography-subtitle2":
    {
      color: `${color.primary_dark} !important`,
    },
  "& .MuiInputBase-root": {
    backgroundColor: `${color.white} !important`,
    color: `${color.black} !important`,
  },
  "& .MuiInputBase-input, & .MuiOutlinedInput-input, & textarea": {
    color: `${color.black} !important`,
    WebkitTextFillColor: `${color.black} !important`,
  },
  "& .MuiInputLabel-root": {
    color: `${color.gray500} !important`,
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: `${color.accent_dark} !important`,
  },
  "& .MuiSelect-select": {
    color: `${color.black} !important`,
    WebkitTextFillColor: `${color.black} !important`,
  },
  "& .MuiSelect-icon": {
    color: `${color.primary_dark} !important`,
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.gray300} !important`,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: `${color.accent} !important`,
    borderWidth: "2px !important",
  },
  "& .MuiButton-root": {
    borderRadius: "10px",
    textTransform: "none" as const,
    fontWeight: 700,
    padding: "9px 17px",
    minHeight: 40,
  },
  "& .MuiButton-contained": {
    backgroundColor: `${color.accent} !important`,
    color: `${color.white} !important`,
    WebkitTextFillColor: `${color.white} !important`,
  },
  "& .MuiButton-contained:hover": {
    backgroundColor: `${color.accent_dark} !important`,
  },
  "& .MuiButton-outlined": {
    backgroundColor: `${color.white} !important`,
    border: `2px solid ${color.accent} !important`,
    color: `${color.primary_dark} !important`,
    WebkitTextFillColor: `${color.primary_dark} !important`,
  },
  "& .MuiButton-outlined:hover": {
    backgroundColor: `${color.orange50} !important`,
  },
  "& .MuiButton-text": {
    color: `${color.primary_dark} !important`,
    WebkitTextFillColor: `${color.primary_dark} !important`,
  },
  "& .MuiButton.Mui-disabled": {
    backgroundColor: `${color.gray200} !important`,
    color: `${color.gray600} !important`,
    WebkitTextFillColor: `${color.gray600} !important`,
    borderColor: `${color.gray300} !important`,
  },
  "& .MuiChip-root": {
    backgroundColor: color.gray100,
    color: color.gray700,
    fontWeight: 600,
  },
  "& .MuiChip-label": {
    color: "inherit !important",
  },
  "& .MuiAlert-message": {
    color: "inherit !important",
  },
  "& .MuiFormHelperText-root": {
    color: `${color.gray500} !important`,
  },
};

const buttonStyle = {
  bgcolor: `${color.accent} !important`,
  color: `${color.white} !important`,
  WebkitTextFillColor: `${color.white} !important`,
  textTransform: "none" as const,
  borderRadius: "9px",
  fontWeight: 700,
  boxShadow: "none",
  "&:hover": {
    bgcolor: `${color.accent_dark} !important`,
  },
  "&.Mui-disabled": {
    bgcolor: `${color.gray200} !important`,
    color: `${color.gray500} !important`,
    WebkitTextFillColor: `${color.gray500} !important`,
  },
};

const HelpDeskAdmin = () => {
  const { t } = useTranslation("helpDesk");
  const statusLabels: Record<TicketStatus, string> = {
    0: t("status.submitted"),
    1: t("status.inProgress"),
    2: t("status.closed"),
  };
  const priorityLabels: Record<TicketPriority, string> = {
    0: t("priority.low"),
    1: t("priority.normal"),
    2: t("priority.high"),
  };
  const [selectedTicketId, setSelectedTicketId] = useState<
    number | undefined
  >();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const {
    data: access,
    isLoading: accessLoading,
    isError: accessError,
  } = useHelpDeskAccess();
  const { data: myTickets = [], isLoading: myTicketsLoading } = useMyTickets();
  const canManage = Boolean(access?.is_admin || access?.is_help_desk);
  const { data: managedTickets = [], isLoading: managedTicketsLoading } =
    useManagedTickets(canManage);
  const tickets = canManage ? managedTickets : myTickets;
  if (accessLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        p={4}
        sx={{ backgroundColor: color.white }}
      >
        <CircularProgress sx={{ color: color.accent }} />
      </Box>
    );
  }
  if (accessError || !access) {
    return <Alert severity="error">{t("admin.accessError")}</Alert>;
  }
  const filteredTickets = tickets.filter((ticket) =>
    [
      ticket.id,
      ticket.title,
      ticket.category,
      ticket.description,
      ticket.workspace?.name,
      ticket.created_by?.name,
      ticket.created_by?.surname,
      ...(ticket.tags ?? []),
    ]
      .join(" ")
      .toLowerCase()
      .includes(search.toLowerCase().trim()),
  );
  const dashboardStats = [
    {
      label: t("summary.totalTickets"),
      count: tickets.length,
      icon: <AssignmentIcon fontSize="small" />,
      bg: "#edf5fb",
      fg: color.secondary,
    },
    {
      label: t("summary.inProgress"),
      count: tickets.filter((x) => x.status === 1).length,
      icon: <AccessTimeIcon fontSize="small" />,
      bg: "#fff4e5",
      fg: color.accent_dark,
    },
    {
      label: t("summary.closed"),
      count: tickets.filter((x) => x.status === 2).length,
      icon: <CheckCircleIcon fontSize="small" />,
      bg: "#eaf7ef",
      fg: "#24764b",
    },
  ];
  const badge = (label: string, bg: string, fg: string) => (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 23,
        borderRadius: "5px",
        fontSize: 10,
        fontWeight: 700,
        backgroundColor: `${bg} !important`,
        color: `${fg} !important`,
        "& .MuiChip-label": { color: "inherit !important" },
      }}
    />
  );
  const panel = {
    backgroundColor: color.white,
    border: "1px solid #e5eaf0",
    borderRadius: "13px",
    boxShadow: "0 3px 14px rgba(3,46,77,.04)",
  };
  return (
    <Box
      sx={{
        ...visibleStyles,
        minHeight: "100vh",
        bgcolor: "#f7f9fc",
        p: { xs: 2, md: 3 },
        "& .MuiTypography-root": { fontFamily: "inherit" },
        "& .MuiOutlinedInput-root": {
          borderRadius: "9px",
          backgroundColor: `${color.white} !important`,
        },
        "& .MuiButton-contained": {
          bgcolor: `${color.accent} !important`,
          color: `${color.white} !important`,
          boxShadow: "none",
          borderRadius: "9px",
        },
        "& .MuiButton-contained:hover": {
          bgcolor: `${color.accent_dark} !important`,
        },
      }}
    >
      {canManage && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {t("admin.managementAccess")}{" "}
          {access.is_admin ? t("admin.adminAccess") : t("admin.helpDeskAccess")}
        </Alert>
      )}
      <Box
        sx={{
          background: "linear-gradient(110deg, #edf5fb, #fff 72%, #fff4e5)",
          border: "1px solid #e5eaf0",
          borderRadius: "15px",
          p: { xs: 2.5, md: 3 },
          mb: 2,
        }}
      >
        <Typography
          sx={{
            color: `${color.secondary} !important`,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 1.2,
          }}
        >
          {t("title")}
        </Typography>
        <Typography
          sx={{
            color: `${color.primary_dark} !important`,
            fontSize: { xs: 24, md: 30 },
            fontWeight: 800,
            mt: 0.5,
          }}
        >
          {t("adminHelp")}
        </Typography>
        <Typography
          sx={{
            color: `${color.gray500} !important`,
            fontSize: 12,
            mt: 0.5,
            mb: 2,
          }}
        >
          {canManage ? t("adminSubtitle") : t("admin.subtitleUser")}
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            fullWidth
            size="small"
            placeholder={t("adminSearchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              "& .MuiInputBase-input": {
                fontSize: "14px",
              },
            }}
            InputProps={{
              startAdornment: (
                <SearchIcon
                  sx={{
                    mr: 1,
                    fontSize: 19,
                    color: color.secondary,
                  }}
                />
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{
              ...buttonStyle,
              px: 2.5,
              whiteSpace: "nowrap",
              fontSize: 12,
            }}
          >
            {t("admin.newTicket")}
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
          gap: 1.5,
          mb: 2,
        }}
      >
        {dashboardStats.map((stat) => (
          <Box
            key={stat.label}
            sx={{
              ...panel,
              p: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.25,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: "9px",
                flexShrink: 0,
                bgcolor: stat.bg,
                color: stat.fg,
                display: "grid",
                placeItems: "center",
              }}
            >
              {stat.icon}
            </Box>
            <Box>
              <Typography
                sx={{
                  color: `${color.gray500} !important`,
                  fontSize: 10.5,
                  fontWeight: 600,
                }}
              >
                {stat.label}
              </Typography>
              <Typography
                sx={{
                  color: `${color.primary_dark} !important`,
                  fontSize: 21,
                  fontWeight: 800,
                  lineHeight: 1.2,
                }}
              >
                {stat.count}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={{ ...panel, p: { xs: 1.25, md: 2 } }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={1.5}
        >
          <Typography
            sx={{
              color: `${color.primary_dark} !important`,
              fontSize: 16,
              fontWeight: 800,
            }}
          >
            {t("adminTitle")}
          </Typography>
          <Typography
            sx={{
              color: `${color.secondary} !important`,
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {filteredTickets.length}
          </Typography>
        </Stack>
        {myTicketsLoading || (canManage && managedTicketsLoading) ? (
          <Box display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: color.accent }} />
          </Box>
        ) : filteredTickets.length === 0 ? (
          <Alert severity="info">
            {search ? t("tickets.emptyDescription") : t("admin.noTickets")}
          </Alert>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box
              component="table"
              sx={{
                width: "100%",
                minWidth: 680,
                tableLayout: "fixed",
                borderCollapse: "collapse",
                "& th": {
                  textAlign: "left",
                  fontSize: 10,
                  fontWeight: 800,
                  color: `${color.gray500} !important`,
                  bgcolor: "#f7f9fc",
                  p: 1,
                  borderBottom: "1px solid #e5eaf0",
                },
                "& td": {
                  p: 1,
                  fontSize: 11,
                  color: `${color.primary_dark} !important`,
                  borderBottom: "1px solid #e5eaf0",
                  overflow: "hidden",
                },
                "& tbody tr": {
                  cursor: "pointer",
                  transition: "background .15s",
                  "&:hover": { bgcolor: "#fff4e5" },
                },
                "& tbody tr:last-child td": { borderBottom: "none" },
              }}
            >
              <colgroup>
                <col style={{ width: "7%" }} />
                <col style={{ width: "21%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "17%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "4%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>{t("table.id")}</th>
                  <th>{t("table.title")}</th>
                  <th>{t("table.fullName")}</th>
                  <th>{t("table.category")}</th>
                  <th>{t("table.tags")}</th>
                  <th>{t("table.status")}</th>
                  <th>{t("table.priority")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredTickets.map((ticket: HelpDeskTicket) => (
                  <tr
                    key={ticket.id}
                    onClick={() => setSelectedTicketId(ticket.id)}
                  >
                    <td>
                      <Typography
                        sx={{
                          color: `${color.secondary} !important`,
                          fontWeight: 800,
                          fontSize: 11,
                        }}
                      >
                        #{ticket.id}
                      </Typography>
                    </td>
                    <td>
                      <Tooltip title={ticket.title}>
                        <Typography
                          noWrap
                          sx={{
                            color: `${color.primary_dark} !important`,
                            fontSize: 11,
                            fontWeight: 700,
                          }}
                        >
                          {ticket.title}
                        </Typography>
                      </Tooltip>
                      <Typography
                        noWrap
                        sx={{
                          color: `${color.gray500} !important`,
                          fontSize: 10,
                        }}
                      >
                        {ticket.workspace?.name ?? t("admin.noProject")}
                      </Typography>
                    </td>
                    <td>
                      <Typography
                        noWrap
                        sx={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: `${color.black} !important`,
                        }}
                      >
                        {ticket.created_by
                          ? `${ticket.created_by.name} ${ticket.created_by.surname}`
                          : t("admin.unknownRequester")}
                      </Typography>
                    </td>
                    <td>
                      <Typography
                        noWrap
                        sx={{
                          fontSize: 11,
                          color: `${color.black} !important`,
                        }}
                      >
                        {ticket.category || "/"}
                      </Typography>
                    </td>
                    <td>
                      <Stack
                        direction="row"
                        flexWrap="wrap"
                        useFlexGap
                        gap={0.4}
                      >
                        {ticket.tags?.length
                          ? ticket.tags.map((tag, i) => (
                              <Tooltip title={tag} key={`${tag}-${i}`}>
                                <Chip
                                  label={`#${tag}`}
                                  size="small"
                                  sx={{
                                    maxWidth: "100%",
                                    height: 20,
                                    borderRadius: "4px",
                                    fontSize: 9,
                                    bgcolor: "#fff4e5",
                                    color: `${color.accent_dark} !important`,
                                  }}
                                />
                              </Tooltip>
                            ))
                          : "/"}
                      </Stack>
                    </td>
                    <td>
                      {badge(
                        statusLabels[ticket.status],
                        ticket.status === 2
                          ? "#eaf7ef"
                          : ticket.status === 1
                            ? "#edf5fb"
                            : "#fff4e5",
                        ticket.status === 2
                          ? "#24764b"
                          : ticket.status === 1
                            ? color.secondary
                            : color.accent_dark,
                      )}
                    </td>
                    <td>
                      {badge(
                        priorityLabels[ticket.priority],
                        ticket.priority === 2
                          ? "#fef0ef"
                          : ticket.priority === 1
                            ? "#edf5fb"
                            : "#f7f9fc",
                        ticket.priority === 2
                          ? "#b42318"
                          : ticket.priority === 1
                            ? color.secondary
                            : color.gray500,
                      )}
                    </td>
                    <td>
                      <ArrowForwardIcon
                        sx={{ color: color.secondary, fontSize: 16 }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </Box>
        )}
      </Box>
      <CreateTicketModal
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onCreated={() => setCreateDialogOpen(false)}
      />
      <AdminTicketEditModal
        ticketId={selectedTicketId}
        onClose={() => setSelectedTicketId(undefined)}
        canManage={canManage}
      />
    </Box>
  );
};
export default HelpDeskAdmin;
